<template>
  <div class="i-career-skillset">
    <iBaseHeader
      highlight-text=""
      text="Useful Skillsets"
      font-size-text="18px"
      align="start"
    />
    <div class="info">
      {{
        activeJobQualifications.hardSkills.length || activeJobQualifications.softSkills.length
          ? "Demonstrating your unique and relevant skillsets could be a great way to demonstrate your ability to execute in the role you are applying for."
          : "No skills requirements found."
      }}
    </div>
    <div
      v-if="activeJobQualifications.hardSkills.length"
      class="main"
    >
      <Carousel
        :autoplay="30000"
        :items-to-scroll="1"
        :items-to-show="isDesktop ? 2 : 1"
        class="reviews-carousel"
        wrap-around
        pause-autoplay-on-hover
        snap-align="start"
      >
        <Slide
          v-for="(entry, index) in activeJobQualifications.hardSkills"
          :key="index"
        >
          <div class="card">
            <div class="top">
              <div
                v-if="!entry.isOptional"
                class="badge"
                :style="{ '--bg-color': riptide500 }"
              >
                Hard Skill
              </div>
              <div class="header">
                {{ entry.title }}
              </div>
              <div class="text">{{ entry.description }}</div>
            </div>
          </div>
        </Slide>
        <template #addons>
          <navigation>
            <template #next>
              <span>
                <img
                  :src="iconArrowRightGray"
                  alt="next-icon"
                />
              </span>
            </template>
            <template #prev>
              <span>
                <img
                  :src="iconArrowLeftGray"
                  alt="prev-icon"
                />
              </span>
            </template>
          </navigation>
        </template>
      </Carousel>
    </div>
    <div
      v-if="activeJobQualifications.softSkills.length"
      class="main"
    >
      <Carousel
        :autoplay="30000"
        :items-to-scroll="1"
        :items-to-show="isDesktop ? 2 : 1"
        class="reviews-carousel"
        wrap-around
        pause-autoplay-on-hover
        snap-align="start"
      >
        <Slide
          v-for="(entry, index) in activeJobQualifications.softSkills"
          :key="index"
        >
          <div
            class="card"
            :class="{ clickable: userStore.isCoachingHasNotEngaged && !userStore.isActiveReportSubscriber }"
            @click="
              userStore.isCoachingHasNotEngaged && !userStore.isActiveReportSubscriber ? linkToCoachSelection() : null
            "
          >
            <div class="top">
              <div
                v-if="!entry.isOptional"
                class="badge"
                :style="{ '--bg-color': purpleHeart400 }"
              >
                Soft Skill
              </div>
              <div class="header">
                {{ entry.title }}
              </div>
              <div class="text">{{ entry.description }}</div>
            </div>
            <div
              v-if="userStore.isCoachingHasNotEngaged && !userStore.isActiveReportSubscriber"
              class="link"
            >
              Hone your soft skills with a coach. <img :src="iconArrowRightBlue" />
            </div>
          </div>
        </Slide>
        <template #addons>
          <navigation>
            <template #next>
              <span>
                <img
                  :src="iconArrowRightGray"
                  alt="next-icon"
                />
              </span>
            </template>
            <template #prev>
              <span>
                <img
                  :src="iconArrowLeftGray"
                  alt="prev-icon"
                />
              </span>
            </template>
          </navigation>
        </template>
      </Carousel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { Carousel, Navigation, Slide } from "vue3-carousel"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { breakpointLg } from "@/styles/variables"
import { purpleHeart400, riptide500 } from "@/styles/colors"

import useIsScreenSize from "@/composables/useIsScreenSize"
import linkToCoachSelection from "@/composables/linkToCoachSelection"

import iBaseHeader from "@/components/base/BaseHeader.vue"

// ASSETS
import iconArrowRightGray from "@/assets/icons/icon-arrow-right-gray.svg"
import iconArrowLeftGray from "@/assets/icons/icon-arrow-left-gray.svg"
import iconArrowRightBlue from "@/assets/icons/icon-blue-arrow-right.svg"

// STORES AND COMPOSABLES
const userStore = useUserStore()
const reportStore = useReportStore()
const isDesktop = useIsScreenSize(breakpointLg)

// COMPUTED
const activeJobQualifications = computed(() => {
  return reportStore.getActiveJobQualifications
})
</script>

<style lang="scss" scoped>
.i-career-skillset {
  color: $pickle-bluewood900;

  @include text-sm;

  margin-bottom: 40px;

  .info {
    @include text-md;

    font-weight: 500;
    color: $comet600;
    margin-top: 15px;
  }

  .main {
    @include flex($direction: column, $justify: center, $align: center);

    padding-top: 30px;

    .reviews-carousel {
      width: calc(100% - 25px);

      @media screen and (min-width: $breakpoint-sm) {
        width: 100%;
      }

      .card {
        @include flex(column, $justify: space-between, $align: start, $gap: 0);

        width: 100%;
        height: 100%;
        padding: 24px;

        @include border-radius-lg;

        background-color: $white;
        border: 1px solid $zumthor100;
        text-align: start;

        @media screen and (min-width: $breakpoint-sm) {
          margin: 0 8px;
        }

        &.clickable {
          cursor: pointer;

          &:hover {
            background-color: $azure-radiance50;
          }
        }

        .top {
          @include flex(column, $justify: start, $align: start, $gap: 15px);

          .badge {
            @include border-radius-lg;

            white-space: nowrap;
            padding: 2px 10px;
            color: $white;
            background-color: var(--bg-color);

            @include text-xs;

            font-weight: 500;
            width: fit-content;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-sm;
            }
          }

          .header {
            @include text-lg;

            font-weight: 700;
            color: $pickle-bluewood900;

            @include flex(row, $justify: start, $align: center, $wrap: wrap, $gap: 6px 15px);

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;
            }
          }

          .text {
            @include text-sm;

            font-weight: 500;
            letter-spacing: 0;
            color: $comet600;
          }
        }

        .link {
          @include flex(row, $justify: space-between, $align: center, $gap: 0);
          @include text-xs;

          font-weight: 500;
          letter-spacing: 0;
          color: $comet600;
          margin-top: 35px;
          width: 100%;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.i-career-skillset {
  /* stylelint-disable */
  .carousel__next,
  .carousel__prev {
    width: 15px;
    margin: 0 -18px;
  }
  /* stylelint-enable */
}

@media print {
  .i-career-skillset {
    .reviews-carousel {
      .card {
        margin: 0 8px;
      }
    }
  }
}
</style>
